/**
 * Several steps selected at once: Ctrl (or Cmd) and a click adds or takes
 * away one step, and Ctrl and a drag across the background draws a box that
 * takes in every step it touches. A group moves together and is deleted
 * together.
 *
 * @module components/workflows/canvas/selection
 */
import type { WorkflowNode } from "@amoreg/contracts";

import { NODE_WIDTH, nodeHeight, type Point } from "./geometry";

export const NO_STEPS: ReadonlySet<string> = new Set();

/** `ids` with `id` added, or taken away when it was already in. */
export function toggleStep(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function unionOf(a: ReadonlySet<string>, b: Iterable<string>): ReadonlySet<string> {
  const next = new Set(a);
  for (const id of b) next.add(id);
  return next;
}

/** The steps a box drawn between two world points touches. */
export function stepsInBox(
  nodes: ReadonlyArray<WorkflowNode>,
  sizes: Readonly<Record<string, number>>,
  from: Point,
  to: Point,
): ReadonlyArray<string> {
  const left = Math.min(from.x, to.x);
  const right = Math.max(from.x, to.x);
  const top = Math.min(from.y, to.y);
  const bottom = Math.max(from.y, to.y);
  return nodes
    .filter(
      (node) =>
        node.position.x <= right &&
        node.position.x + NODE_WIDTH >= left &&
        node.position.y <= bottom &&
        node.position.y + nodeHeight(node, sizes) >= top,
    )
    .map((node) => node.id);
}

/** Where each step of a group lands when the group is moved by `delta`. */
export function movedBy(nodes: ReadonlyArray<WorkflowNode>, ids: ReadonlySet<string>, delta: Point): ReadonlyMap<string, Point> {
  return new Map(
    nodes.filter((node) => ids.has(node.id)).map((node) => [node.id, { x: node.position.x + delta.x, y: node.position.y + delta.y }] as const),
  );
}
